/* OnSumn — MerchPopover
 *
 * In-page Liquid Glass merch preview that MORPHS out of the MERCH chip
 * via the FLIP technique (First, Last, Invert, Play).
 *
 * EXTRACTED PATTERN
 * ─────────────────
 * The popular `MorphingPopover` (motion/react + shadcn/Tailwind/TS)
 * component uses Framer Motion's `layoutId` prop to share an animation
 * lineage between two DOM nodes. Under the hood Framer does FLIP:
 *
 *   First   — measure the trigger's rect at click time
 *   Last    — let the popover mount at its final size + position
 *   Invert  — set the popover's initial transform so visually it starts
 *             AT the trigger (translate(dx, dy) scale(sx, sy))
 *   Play    — clear the transform next frame, let CSS transitions
 *             animate to identity
 *
 * That's the entire mechanism. No `motion` dep, no Tailwind, no
 * TypeScript — just getBoundingClientRect + transform + transition,
 * all of which the browser does natively.
 *
 * The trigger captures its rect with chip.getBoundingClientRect() and
 * passes it via props. This component mounts at full size on render,
 * then useLayoutEffect runs the FLIP inversion + transition.
 *
 * BRAND
 * ─────
 * Brutalist-editorial composition + Liquid Glass material (mirrors
 * /merch-preview.html so the in-page morph and the standalone URL
 * stay visually identical). Theme-aware via the same darkMode prop
 * the parent EventCard passes down.
 *
 * A11Y
 * ────
 * - role="dialog" + aria-modal + explicit aria-label ({artist} merch preview →
 *   Merch preview for {title} → Merch preview); NO aria-labelledby — it would
 *   outrank the label and the h1 would be the name (2026-10-02). h1 keeps its id.
 * - Esc + backdrop click close (with reverse-morph)
 * - Hard Tab/Shift-Tab focus trap while open (lib/focus-trap.js, shared
 *   with RulesPopover — the 2026-08-20 parity pass closing the "shared
 *   skeleton gap" debt logged in the venue-rules spec §4.6)
 * - 44px touch-target close button
 * - prefers-reduced-motion → skip morph, fade only
 * - body scroll lock while open
 * - Focus moves to close button on mount; restores to trigger on close
 *
 * FTC + AFFILIATE (Atlas guardrails — same as the popup variant)
 * ──────────────────────────────────────────────────────────────
 * - Persistent "OFFICIAL STORE · ARTIST" disclosure header
 * - All outbound links: rel="sponsored nofollow noopener noreferrer"
 * - Roman Barlow disclosure footer (no algorithmic italic)
 */

import { trapDialogTab } from '../lib/focus-trap.js';

function MerchPopover({ event, darkMode, triggerRect, onClose }) {
  const panelRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);
  const previouslyFocusedRef = React.useRef(null);
  // Closing guard as a REF, not state — ported back from RulesPopover in
  // the 2026-08-20 a11y parity pass (this WAS the logged follow-up debt).
  // The Esc listener below is attached in a []-dep effect, so it captures
  // the FIRST render's handleClose; a useState guard is a stale `false`
  // inside that closure forever, and a double-Esc mid-morph fires onClose
  // twice. A ref reads live through any stale closure, so the guard
  // actually guards (tests/popover-focus-trap.test.js pins this).
  const closingRef = React.useRef(false);

  const reducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── FLIP morph (open) ───
  // Runs synchronously after mount, before paint, so the user never
  // sees the panel in its final position before the morph plays.
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (reducedMotion || !triggerRect) {
      // No motion — just fade up. Same content arrives, faster, no morph.
      panel.style.transition = 'opacity 200ms ease';
      panel.style.opacity = '0';
      panel.getBoundingClientRect();
      requestAnimationFrame(() => { panel.style.opacity = '1'; });
      return;
    }

    // LAST — measure where the panel ends up at its natural position.
    const toRect = panel.getBoundingClientRect();

    // INVERT — math to start the panel exactly where the chip was.
    // Clamp scale floor so the morph starts visible at the chip size
    // (a chip is ~50×24 → ~0.06 of a 720×540 panel; that's fine).
    const dx = triggerRect.left - toRect.left;
    const dy = triggerRect.top - toRect.top;
    const sx = Math.max(0.05, triggerRect.width / toRect.width);
    const sy = Math.max(0.05, triggerRect.height / toRect.height);

    panel.style.transformOrigin = 'top left';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0.85';

    // Force a layout flush so the initial transform paints before the
    // transition starts. Reading any layout-affecting prop does this.
    panel.getBoundingClientRect();

    // PLAY — clear the transform and let CSS transitions animate to identity.
    requestAnimationFrame(() => {
      panel.style.transition =
        'transform 420ms cubic-bezier(0.16, 1, 0.3, 1), ' +
        'opacity 240ms ease 60ms';
      panel.style.transform = 'translate(0, 0) scale(1, 1)';
      panel.style.opacity = '1';
    });
  }, [triggerRect, reducedMotion]);

  // ─── Esc + focus trap + body scroll lock + focus management ───
  React.useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    // Escape closes from anywhere; Tab/Shift-Tab hard-cycle inside the
    // dialog (lib/focus-trap.js — panelRef is read at keydown time, so
    // this []-dep closure can't go stale). Keyboard-only: pointer
    // interactions never route through here, so mouse focus is untouched.
    const onKey = (e) => {
      if (e.key === 'Escape') { handleClose(); return; }
      trapDialogTab(panelRef.current, e);
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus to close button (delayed so the morph starts first).
    const focusTimer = setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 200);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
      // Restore focus to the chip that opened us (if still in DOM).
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        try { previouslyFocusedRef.current.focus(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── FLIP morph (close) — reverse direction, ref-guarded ───
  const handleClose = React.useCallback(() => {
    if (closingRef.current) return;
    const panel = panelRef.current;
    if (!panel || reducedMotion || !triggerRect) {
      closingRef.current = true;
      onClose();
      return;
    }
    closingRef.current = true;

    const fromRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - fromRect.left;
    const dy = triggerRect.top - fromRect.top;
    const sx = Math.max(0.05, triggerRect.width / fromRect.width);
    const sy = Math.max(0.05, triggerRect.height / fromRect.height);

    panel.style.transition =
      'transform 320ms cubic-bezier(0.4, 0, 1, 1), opacity 200ms ease 80ms';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';

    setTimeout(() => onClose(), 320);
  }, [onClose, reducedMotion, triggerRect]);

  if (!event || !event.merch) return null;
  const merch = event.merch;
  const items = Array.isArray(merch.items) ? merch.items : [];
  const itemCount = items.length;
  const artist = (merch.artist || 'Artist').toUpperCase();
  const host = (() => {
    try { return new URL(merch.shop_url).hostname.replace(/^www\./, ''); }
    catch { return ''; }
  })();

  // ─── Theme-aware tokens ───
  // Mirror /merch-preview.html: amber-readable drops to a deeper burnt
  // amber in light mode to clear WCAG AA 4.5:1 on the cream surface.
  const surface = darkMode ? '#121414' : '#F8F8F8';
  const onSurface = darkMode ? '#E2E2E2' : '#1A1A1A';
  const muted = darkMode ? 'rgba(226, 226, 226, 0.58)' : 'rgba(26, 26, 26, 0.62)';
  const amberReadable = darkMode ? '#D57800' : 'oklch(0.46 0.13 60)';
  const ruleColor = darkMode ? 'rgba(213, 120, 0, 0.55)' : 'rgba(139, 74, 0, 0.6)';
  const hairline = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const tileImgBg = darkMode ? 'oklch(0.22 0.01 70)' : 'oklch(0.965 0.005 70)';
  const ctaShadow = darkMode ? '#0a0a0a' : 'oklch(0.35 0.12 60)';

  const panelGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'panel')
    : { background: surface };

  const btnGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'btn')
    : {};

  const stop = (e) => e.stopPropagation();

  // Portal to document.body so the popover escapes the EventCard's
  // ancestor stacking/transform context. EventCard has `overflow: hidden`
  // and `transform: translateY(...) scale(...)` on hover — both create
  // containing blocks that would trap `position: fixed` to the card
  // instead of the viewport. Portaling lifts the popover out of that
  // chain so it centers on screen.
  const overlay = (
    <div
      role="presentation"
      onClick={(e) => { stop(e); handleClose(); }}
      style={{
        // z=2000 sits above the Spotlight search surfaces (SearchPanel=1001,
        // SearchDetailPopover=1050) so MerchPopover opened from a search
        // result overlays cleanly. Still below FilterPopup (9990) which is
        // a distinct user gesture that can't be racing this surface.
        position: 'fixed', inset: 0, zIndex: 2000,
        background: darkMode ? 'rgba(8, 8, 10, 0.55)' : 'rgba(40, 30, 20, 0.45)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(12px, 4vw, 32px)',
        animation: reducedMotion ? 'none' : 'merch-backdrop-in 220ms ease both',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={merch.artist ? `${merch.artist} merch preview` : event.title ? `Merch preview for ${event.title}` : 'Merch preview'}
        onClick={stop}
        style={{
          ...panelGlass,
          // Light-mode override: GLASS.light's layered ~0.64-effective white
          // reads thin over busy event grids — bump to a near-opaque white so
          // the popover sits forward of the page without losing the glass
          // material (blur + inner glow from panelGlass stay intact). Dark
          // mode is untouched; the original layered gradient reads strong
          // enough already.
          ...(!darkMode && { background: 'rgba(255, 255, 255, 0.96)' }),
          border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 24,
          width: 'min(720px, 100%)',
          maxHeight: '92vh', overflowY: 'auto',
          padding: 'clamp(20px, 3vw, 28px)',
          position: 'relative',
          willChange: 'transform, opacity',
          boxShadow: (panelGlass.boxShadow || '') +
            ', 0 30px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04)',
        }}
      >
        {/* Top bar — issue stamp + glass close button */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, marginBottom: 'clamp(20px, 3vw, 32px)',
        }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: amberReadable,
            padding: '5px 10px',
            border: `1.5px solid ${amberReadable}`,
            borderRadius: 2,
            transform: 'rotate(-1.2deg)', transformOrigin: 'left center',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 56px)',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>ISSUE №01 · MERCH</span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={handleClose}
            aria-label="Close merch preview"
            style={{
              ...btnGlass,
              width: 44, height: 44, borderRadius: '50%',
              border: darkMode ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.1)',
              color: onSurface,
              fontFamily: 'var(--font-display)',
              fontSize: 20, fontWeight: 700, lineHeight: 1, padding: 0,
              cursor: 'pointer', flex: '0 0 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: reducedMotion ? 'none' : 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onMouseEnter={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(90deg)'; }}
            onMouseLeave={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(0deg)'; }}
          >×</button>
        </div>

        {/* Kicker + item count tail */}
        <p style={{
          margin: '0 0 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: amberReadable, lineHeight: 1.3,
        }}>
          Official store · {artist}
          <span aria-hidden="true" style={{
            flex: '1 1 auto', height: 1, maxWidth: 180, minWidth: 24,
            background: ruleColor,
          }} />
          {itemCount > 0 && (
            <span style={{
              flex: '0 0 auto',
              fontSize: 10, letterSpacing: '0.16em',
              color: muted,
            }}>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
          )}
        </p>

        {/* Display headline */}
        <h1 id="merch-headline" style={{
          fontFamily: 'var(--font-display)', fontWeight: 900,
          fontSize: 'clamp(48px, 9vw, 88px)', lineHeight: 0.92,
          letterSpacing: '-0.025em',
          color: onSurface, margin: 0,
          textTransform: 'uppercase',
          textWrap: 'balance',
        }}>
          WEAR IT<br />
          TO THE SHOW<span style={{ color: 'var(--international-orange)' }}>.</span>
        </h1>

        {/* Byline */}
        <p style={{
          margin: '16px 0 0',
          display: 'flex', gap: 8, alignItems: 'baseline',
          fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
          color: muted, lineHeight: 1.45,
        }}>
          <span aria-hidden="true" style={{ color: amberReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
          <span>
            <strong style={{ color: onSurface, fontWeight: 700 }}>{event.title || 'Tour merch'}</strong>
            {event.venue ? ` · ${event.venue}` : ''}
            {event.date ? ` · ${event.date}${event.time ? ', ' + event.time : ''}` : ''}
          </span>
        </p>

        {/* Amber editorial rule */}
        <hr aria-hidden="true" style={{
          border: 0, height: 1, background: ruleColor,
          margin: 'clamp(24px, 3vw, 36px) 0',
        }} />

        {/* Product strip */}
        <section aria-label="Merch items" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'clamp(12px, 2.5vw, 22px)',
        }}>
          {items.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              fontFamily: 'var(--font-body)', fontSize: 14,
              color: muted, padding: '24px 0',
            }}>No items to preview right now.</div>
          ) : items.map((it, i) => (
            <a
              key={i}
              href={it.product_url || merch.shop_url}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
              onClick={stop}
              aria-label={
                (it.title || 'Merch item') +
                (typeof it.price_min === 'number' ? ` — $${it.price_min}` : '') +
                ' — opens artist store in new tab'
              }
              style={{
                display: 'flex', flexDirection: 'column',
                textDecoration: 'none', color: 'inherit',
                borderRadius: 2,
                transition: reducedMotion ? 'none' : 'transform 240ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              onMouseEnter={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{
                aspectRatio: '1 / 1',
                background: tileImgBg,
                border: `1px solid ${hairline}`,
                overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {it.image_url ? (
                  <img
                    src={it.image_url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'contain', padding: '8%',
                      transition: reducedMotion ? 'none' : 'transform 360ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  />
                ) : null}
              </div>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  lineHeight: 1.3, color: onSurface,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', minHeight: '2.6em',
                }}>{it.title || ''}</div>
                {typeof it.price_min === 'number' && (
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 800,
                    letterSpacing: '0.02em', color: amberReadable,
                  }}>${it.price_min}</div>
                )}
              </div>
            </a>
          ))}
        </section>

        {/* Amber editorial rule */}
        <hr aria-hidden="true" style={{
          border: 0, height: 1, background: ruleColor,
          margin: 'clamp(24px, 3vw, 36px) 0',
        }} />

        {/* CTA — international-orange brick with hard offset shadow */}
        <a
          href={merch.shop_url}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          onClick={stop}
          aria-label={`Visit ${merch.artist || 'artist'} official store, opens in new tab`}
          style={{
            display: 'block', textAlign: 'center', textDecoration: 'none',
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            padding: '18px 20px',
            background: 'var(--international-orange)',
            color: 'var(--on-international-orange)', border: 0, borderRadius: 0,
            boxShadow: `6px 6px 0 0 ${ctaShadow}`,
            transition: reducedMotion ? 'none' : 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
          }}
          onMouseEnter={(e) => {
            if (reducedMotion) return;
            e.currentTarget.style.transform = 'translate(2px, 2px)';
            e.currentTarget.style.boxShadow = `4px 4px 0 0 ${ctaShadow}`;
            e.currentTarget.style.background = 'var(--international-orange-bright, #FF5717)';
          }}
          onMouseLeave={(e) => {
            if (reducedMotion) return;
            e.currentTarget.style.transform = 'translate(0, 0)';
            e.currentTarget.style.boxShadow = `6px 6px 0 0 ${ctaShadow}`;
            e.currentTarget.style.background = 'var(--international-orange)';
          }}
        >
          Visit official store <span aria-hidden="true">→</span>
          {host && (
            <span style={{
              display: 'inline-block',
              marginLeft: 8, paddingLeft: 8,
              borderLeft: '1px solid rgba(16,17,15,0.35)',
              fontWeight: 700, color: 'var(--on-international-orange)',
              letterSpacing: '0.08em',
            }}>{host}</span>
          )}
        </a>

        {/* FTC disclosure footer */}
        <p style={{
          margin: '16px 0 0',
          display: 'flex', gap: 8,
          fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
          color: muted, lineHeight: 1.55, letterSpacing: '0.01em',
        }}>
          <span aria-hidden="true" style={{ color: amberReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
          OnSumn doesn't sell merch — links open the artist's site. Pull up the way you wanna pull up.
        </p>
      </div>
    </div>
  );

  // Portal escape: ReactDOM.createPortal mounts `overlay` as a direct
  // child of document.body, sidestepping any transform/overflow/filter
  // ancestor that would otherwise contain `position: fixed`. Falls back
  // to in-tree render if ReactDOM isn't on the page (defensive — every
  // OnSumn HTML entry loads it).
  if (typeof window !== 'undefined'
      && window.ReactDOM
      && typeof window.ReactDOM.createPortal === 'function'
      && typeof document !== 'undefined') {
    return window.ReactDOM.createPortal(overlay, document.body);
  }
  return overlay;
}

// Backdrop fade-in keyframes — injected once on first MerchPopover load.
if (typeof document !== 'undefined' && !document.getElementById('merch-popover-keyframes')) {
  const style = document.createElement('style');
  style.id = 'merch-popover-keyframes';
  style.textContent = '@keyframes merch-backdrop-in { from { opacity: 0; } to { opacity: 1; } }';
  document.head.appendChild(style);
}

window.MerchPopover = MerchPopover;
